import { Fragment, useEffect, useRef, useState } from "react";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassFoldUdBox, KeyValueLine, KeyValuesBoxGrid, KeyValuesHorizontal, PropertyTextDoc, ValueGroup, ViewButtonIcon, ViewButtonText } from "./PropertyComponents";
import { PropertyData } from "./PropertyInfoCollectorTypes";
import { TingBogRecordNumber } from "./PropertyInfoGroupFormatters";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import { BiBug } from "react-icons/bi";
import { DC } from "src/dataCollector/DataCollector";
import { titleValParser } from "./PropertyInfoParser";

export function AdkomstDetailsView(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const adkomst = props.managedWindow.windowParams?.adkomst as (PropertyData["ViamapTingBog"] & {})["adkomster"][number]

   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [xmlDoc, setXmlDoc] = useState<Document | null>(null);
   // (no per-row hover state needed; use native tooltip for showing BilagReference id)

   async function fetchServitutDetailsByUuid(uuid: string, abortSignal: AbortSignal) {
      setLoading(true);
      setError(null);
      DC.TL.DokumentTingbog(uuid).then((data) => {
         if (abortSignal.aborted) return;
         setXmlDoc(data);
      }).catch((err) => {
         if (abortSignal.aborted) return;
         setXmlDoc(null);
         setError(err.message || String(err));
      }).finally(() => {
         setLoading(false);
      });
   }


   useEffect(() => {
      if (!adkomst.dokumentId) return;
      let abort = new AbortController();
      fetchServitutDetailsByUuid(adkomst.dokumentId, abort.signal);
      return () => {
         abort.abort();
      };
   }, [adkomst.dokumentId]);

   let adkomstTingbogLink = getSkoedeLink(adkomst);

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Adkomst details")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         subHeaderContent={
            <>
               {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ? (
                  <>
                     <ViewButtonIcon onClick={() => console.info(xmlDoc)} ><BiBug /><span style={{ paddingInline: "5px 5px" }} >Log to Console</span></ViewButtonIcon>
                  </>
               ) : null}
               <ViewButtonIcon
                  title={adkomstTingbogLink ? undefined : Localization.getText("No link available")}
                  disabled={!adkomstTingbogLink}
                  onClick={() => {
                     window.open(adkomstTingbogLink, "_blank");
                  }}
               >
                  <span style={{ paddingInline: "5px" }}>Åbn på Tinglysning.dk</span>
               </ViewButtonIcon>
            </>
         }
      >
         <ADModalBody >
            {xmlDoc ? <DokumentHeader xmlDoc={xmlDoc} originalTinglysningsDato={adkomst?.tinglysningsDato} /> : null}
            {loading ? <ShowLoading link={adkomstTingbogLink} /> : null}
            {error ? <ShowError error={error} /> : null}
            {xmlDoc ? (
               <>
                  <ShowTinglysningMessage xmlDoc={xmlDoc} link={adkomstTingbogLink} />
                  <ServitutEjendomme servitutXDOC={xmlDoc} />
                  <SkoedeOpsamling servitutXDOC={xmlDoc} />
                  <SkoedeTekst servitutXDOC={xmlDoc} />
                  <BilagsVisning servitutXDOC={xmlDoc} />
                  {SettingsManager.getSystemSetting("debuggingShowExtraDebugging", false) ? < EkstraInfoBox xmlDoc={xmlDoc} /> : null}
               </>
            ) : null}
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ShowLoading({ link }: { link?: string }) {
   let textRef = useRef<HTMLDivElement>(null);
   let [showQuickButton, setShowQuickButton] = useState(false);

   useEffect(() => {
      if (!textRef.current) return;
      const box = textRef.current;
      function updateText() {
         box.innerText = Localization.getText("The action takes longer than expected");
         setShowQuickButton(true);
      }
      const interval = setTimeout(() => updateText(), 5000);
      box.innerText = Localization.getText("Loading");
      return () => {
         clearTimeout(interval);
      }
   }, [textRef]);

   return (
      <GlassInfo>
         <div ref={textRef} style={{ padding: 8 }}></div>
         <Spinner animation="border" role="status" />
         {showQuickButton && link ? <><br /><ViewButtonText style={{ marginTop: "1em" }}
            onClick={() => {
               window.open(link, "_blank");
            }}

         >Åbn på Tinglysning.DK</ViewButtonText></> : null}
      </GlassInfo>
   )
}

function ShowError({ error }: { error: string }) {
   return (
      <GlassInfo>
         <div style={{ padding: 8, color: "var(--dangerColor)" }}>{error}</div>
      </GlassInfo>
   )
}

function ShowTinglysningMessage({ xmlDoc, link }: { xmlDoc: Document | null, link?: string }) {
   if (!(xmlDoc instanceof Document) || xmlDoc.children[0]?.children.length !== 0) {
      return <></>
   }
   return (
      <GlassInfo>
         {xmlDoc?.children[0].textContent}
         {link ? <><br /><ViewButtonText style={{ marginTop: "1em" }}
            onClick={() => {
               window.open(link, "_blank");
            }}
         >Åbn på Tinglysning.DK</ViewButtonText></> : null}
      </GlassInfo>
   )
}

function DokumentHeader({ xmlDoc, originalTinglysningsDato }: { xmlDoc: Document, originalTinglysningsDato?: string }) {
   if (!(xmlDoc instanceof Document)) {
      return <></>
   }

   const timeParser = function () {
      let date = Intl.DateTimeFormat(Localization.getLocale(), { year: 'numeric', month: '2-digit', day: '2-digit' }).format;
      return (val?: string) => {
         return val ? date(new Date(val.split("+")[0])) : "";
      }
   }();

   const formatDate = (val?: string) =>
      val ? new Date(val).toLocaleDateString(Localization.getLocale(), { year: 'numeric', month: '2-digit', day: '2-digit' }) : "";

   const normalizeDatePart = (val?: string | null) => (val?.split("T")[0] || "");

   const documentAliasIdentifikator = (xmlDoc.getElementsByTagName("DokumentAliasIdentifikator")[0]?.textContent?.split("-")[0]) || "";
   let originalDateOnly: string | undefined;
   if (/^\d{8}$/.test(documentAliasIdentifikator)) {
      const y = documentAliasIdentifikator.slice(0, 4), m = documentAliasIdentifikator.slice(4, 6), d = documentAliasIdentifikator.slice(6, 8);
      originalDateOnly = formatDate(`${y}-${m}-${d}`);
   } else {
      originalDateOnly = originalTinglysningsDato ? formatDate(normalizeDatePart(originalTinglysningsDato)) : undefined;
   }

   const lastSignedRaw = xmlDoc.getElementsByTagName("TinglysningsDato")[0]?.textContent || "";
   const lastSignedDateOnly = formatDate(normalizeDatePart(lastSignedRaw));
   const shouldShowLastSigned = Boolean(lastSignedRaw) && (originalDateOnly !== lastSignedDateOnly);

   return (
      <KeyValuesHorizontal >
         <KeyValueLine valKey={"DokumentID"} value={xmlDoc.getElementsByTagName("DokumentIdentifikator")[0]?.textContent || ""} />
         <KeyValueLine valKey={Localization.getText("Date/serial number")} value={TingBogRecordNumber(xmlDoc.getElementsByTagName("DokumentAliasIdentifikator")[0]?.textContent || "")} />
         {shouldShowLastSigned ? <KeyValueLine valKey={<span style={{ color: "var(--dangerColor)" }}>{Localization.getText("Last signed")}</span>} value={lastSignedDateOnly} /> : null}
         <KeyValueLine valKey={"Overtagelsesdato"} value={timeParser(xmlDoc.getElementsByTagName("SkoedeOvertagelsesDato")[0]?.textContent || "")} />
         <KeyValueLine valKey={"SkoedeKoebsaftaleDato"} value={timeParser(xmlDoc.getElementsByTagName("SkoedeKoebsaftaleDato")[0]?.textContent || "")} />
      </KeyValuesHorizontal >
   )
}

function ServitutEjendomme({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }

   let Ejendomme = Array.from(servitutXDOC.getElementsByTagName("FastEjendomMedRettigheder"));
   if (Ejendomme.length === 0) {
      return <></>
   }

   return (
      <GlassFoldUdBox title={Localization.getText("Properties") + " (" + Ejendomme.length + ")"} foldetUd={false}>
         {Ejendomme.map((ejendom, index) => (
            <Fragment key={index}>
               <ValueGroup>
                  <KeyValueLine valKey={"BFEnummer"} value={ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || ""} />
                  <KeyValuesBoxGrid minWidth={"150px"} >
                     {Array.from(ejendom.getElementsByTagName("Matrikel")).map((matrikel, idx) => {
                        const name = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent || "";
                        const matrNr = String(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent ?? "")?.replace(/^0*/, "");
                        return (
                           <Fragment key={idx}>
                              <KeyValueLine valKey={"Matnr. " + matrNr} value={name} keepEmpty />
                           </Fragment>
                        )
                     })}
                  </KeyValuesBoxGrid>
               </ValueGroup>
            </Fragment>
         ))}
      </GlassFoldUdBox>
   )

   return ""
}

function ServitutTekstGrupper({ servitutXDOC }: { servitutXDOC: Element }) {
   let header = servitutXDOC.getElementsByTagName("Overskrift")[0];
   let paragraphs = servitutXDOC.getElementsByTagName("Afsnit");
   return (<>
      {header ? (
         <h1>
            {header.textContent}
         </h1>
      ) : null}
      {[...paragraphs].map((p, idx) => (
         <p key={idx} >
            {p.textContent}
         </p>
      ))}
   </>
   )
}

function EkstraInfoBox({ xmlDoc }: { xmlDoc: Document }) {
   if (!(xmlDoc instanceof Document)) {
      return <></>
   }

   let senesteJordRegisterring = Array.from(xmlDoc.getElementsByTagName("LandParcelRegistrationDate")).map(el => el.textContent).sort().reverse()[0];

   return (
      <GlassFoldUdBox title={"Ekstra Information "} foldetUd={false}>
         <KeyValueLine valKey={"Seneste Jordregistering"} value={senesteJordRegisterring || Localization.getText("No data")} />
      </GlassFoldUdBox>
   )


}

function SkoedeOpsamling({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let Skoede = servitutXDOC.getElementsByTagName?.("AdkomstStruktur")[0];
   if (!Skoede) {
      return <></>
   }

   return (
      <GlassFoldUdBox title={Localization.getText("Skoede Summary")} foldetUd={true}>
         <SkoedeAdkomsttype liste={Skoede} />
         <SkoedeEjendomsKategori liste={Skoede?.getElementsByTagName("EjendomKategori")[0] || null} />
         <SkoedeSubListe liste={Skoede?.getElementsByTagName("EjendomstypeSub")[0] || null} />
         <SkoedeSubListe liste={Skoede?.getElementsByTagName("SkoedeKoebesum")[0] || null} />
         <SkoedeUdenKoebesum liste={Skoede?.getElementsByTagName("SkoedeUdenforKoebesum")[0] || null} />
      </GlassFoldUdBox>
   )
}

function SkoedeAdkomsttype({ liste }: { liste: Element | null }) {
   return (
      <ValueGroup>
         <KeyValueLine valKey={"Adkomsttype"} CParser={TLCParser} value={liste?.getElementsByTagName("AdkomstType")[0]?.textContent || ""} />
         <KeyValueLine valKey={"Handelstype"} CParser={TLCParser} value={liste?.getElementsByTagName("AdkomstHandelKode")[0]?.textContent || ""} />
      </ValueGroup>
   )
}

function SkoedeEjendomsKategori({ liste }: { liste: Element | null }) {
   if (!liste || liste.children.length === 0) {
      return <></>
   }
   let xmlList = liste.children[0];
   let ByggemodningsomkostningIndikator = xmlList.getElementsByTagName("ByggemodningsomkostningIndikator")[0]?.textContent;
   return (
      <ValueGroup>
         <KeyValueLine valKey={"Ejendomskategori"} value={xmlList.tagName.replace("EjendomKategori", "") || ""} />
         {ByggemodningsomkostningIndikator !== undefined ? <KeyValueLine valKey={"ByggemodningsomkostningIndikator"} value={ByggemodningsomkostningIndikator} /> : null}
         <KategoriBeskrivelse EjendomsKategori={xmlList.getElementsByTagName("EjendomKategoriBeskrivelse")?.[0] || null} />
      </ValueGroup>
   );
}

function KategoriBeskrivelse({ EjendomsKategori }: { EjendomsKategori: Element | null }) {
   if (!EjendomsKategori) {
      return <></>;
   }

   return (
      <GlassFoldUdBox variant="PropertyLine" title={"Beskrivelse"} foldetUd={true} >
         <PropertyTextDoc>
            <ServitutTekstGrupper servitutXDOC={EjendomsKategori!} />
         </PropertyTextDoc>
      </GlassFoldUdBox>
   )
}

function SkoedeSubListe({ liste }: { liste: Element | null }) {
   if (!liste) {
      return <></>
   }
   let xmlList = liste.children;
   return (<ValueGroup>
      {Array.from(xmlList).map((et, idx) => {
         let CurrencyFormatter = Intl.NumberFormat(Localization.getLocale(), { style: "currency", currency: "DKK", maximumFractionDigits: 2, minimumFractionDigits: 2 }).format
         let AreaFormatter = function () {
            let x = Intl.NumberFormat(Localization.getLocale(), { maximumFractionDigits: 2, minimumFractionDigits: 0 }).format;
            return (val) => {
               return x(val) + " m²";
            }
         }();
         if (et.tagName.includes("Tekst")) {
            return (<Fragment key={idx} >
               <GlassFoldUdBox title={titleValParser(et.tagName)} variant="PropertyLine" foldetUd={true} >
                  <PropertyTextDoc>
                     <ServitutTekstGrupper servitutXDOC={et} />
                  </PropertyTextDoc>
               </GlassFoldUdBox>
            </Fragment>
            )
         }

         return (
            <Fragment key={idx} >
               <KeyValueLine key={idx} valKey={et.localName || et.nodeName} value={et.textContent || ""}
                  CParser={(k, val) => {
                     if (k.includes("Areal")) {
                        return AreaFormatter(parseFloat(val));
                     } else {
                        return CurrencyFormatter(parseFloat(val));
                     }
                  }}
               />
            </Fragment>
         )
      })}
   </ValueGroup>
   )
}

function SkoedeUdenKoebesum({ liste }: { liste: Element | null }) {
   if (!liste) {
      return (<></>)
   }

   let xmlList = liste.children;
   return (
      <GlassFoldUdBox title={"Udenfor Købesum"} foldetUd={false} >
         {Array.from(xmlList).map((et, idx) => {
            return (
               <Fragment key={idx} >
                  <KeyValueLine key={idx} valKey={et.localName || et.nodeName} CParser={(k, val) => (((parseFloat(val) as number).toLocaleString(Localization.getLocale(), { style: "currency", currency: "DKK", maximumFractionDigits: 2, minimumFractionDigits: 2 })))} value={et.getElementsByTagName("Beloeb")[0]?.textContent || ""} />
               </Fragment>
            )
         })}
      </GlassFoldUdBox>
   )
}

function SkoedeTekst({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let servitut = servitutXDOC.getElementsByTagName?.("SkoedeTekst")[0];
   let servitutTextGroups = [...servitut?.getElementsByTagName("TekstGruppe") || []];
   if (servitutTextGroups.length === 0) {
      return <></>
   }
   let firstTextGroup = servitutTextGroups[0];
   let header = firstTextGroup.getElementsByTagName("Overskrift")[0];
   let paragraphs = firstTextGroup.getElementsByTagName("Afsnit");
   return (
      <GlassFoldUdBox title={"Skøde - " + (header?.textContent || paragraphs[0]?.textContent)} foldetUd={false}>
         <PropertyTextDoc>
            {servitutTextGroups.map((tg, idx) => {
               return (
                  <Fragment key={idx}>
                     {(tg.children.length > 1 || idx != 0) && (
                        <ServitutTekstGrupper key={idx} servitutXDOC={tg} />
                     )}
                  </Fragment>
               )
            })}
         </PropertyTextDoc>
      </GlassFoldUdBox>
   )
}


function BilagsVisning({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let samling = servitutXDOC!.getElementsByTagName("BilagReferenceSamling")[0];
   if (!samling) return null;

   return (
      <GlassFoldUdBox title={Localization.getText("AppendixReference")} foldetUd={true}>
         {Array.from(samling.children).map((r: Element, idx: number) => {
            let ref = r.getElementsByTagName("BilagReference")[0]?.textContent || "";
            let kode = r.getElementsByTagName("BilagReferenceKode")[0]?.textContent || "";
            let beskrivelse = r.getElementsByTagName("Afsnit")[0]?.textContent || "";

            const linkUrl = ref ? `https://www.tinglysning.dk/tinglysning/rest/bilag/${ref}` : null;
            const lang = typeof Localization.getLanguage === 'function' ? Localization.getLanguage() : 'da';
            const translatedKode = kode ? (translationTableBilagsReferenceKode?.[lang]?.[kode] ?? translationTableBilagsReferenceKode?.['da']?.[kode] ?? kode) : null;

            return (
               <Fragment key={ref}>
                  <KeyValueLine valKey={
                     linkUrl ? (
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" >
                           Bilag {idx + 1}
                        </a>
                     ) : (
                        beskrivelse || Localization.getText("No data")
                     )
                  } value={<>
                     <span>
                        {beskrivelse}
                     </span><br />
                     <span>
                        {translatedKode}
                     </span>
                  </>} infoText={ref} />
               </Fragment>
            );
         })}
      </GlassFoldUdBox>
   );
}

function getSkoedeLink(adkomst?: (PropertyData["ViamapTingBog"] & {})["adkomster"][number]): string {
   if (adkomst?.aktId) return "";
   return `https://www.tinglysning.dk/tinglysning/rest/pdf/atd/${adkomst?.dokumentId}`;
}

// Translation table for BilagReferenceKode (updated)
const translationTableBilagsReferenceKode: any = {
   da: {
      "1": "Udlægsattest, udstedt før 25. marts 2008",
      "2": "Arrestattest, udstedt før 25. marts 2008",
      "3": "Ejendomsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "4": "Modifikationsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "5": "Auktionsskøde med evt. transportskøde",
      "6": "Bobestyrerattest, udstedt før 25. marts 2008",
      "7": "Kuratorattest, udstedt før 25. marts 2008",
      "8": "Tilladelse til erhvervelse af fast ejendom i Danmark, udstedt af JM",
      "9": "Tilladelse til erhvervelse, udstedt af Statsamt",
      "10": "Tilladelse fra Skov- og Naturstyrelsen om selskabs erhvervelse af fast ejendom",
      "11": "Dokumentation fra menighedsråd om tegningsreglerne for rådet",
      "12": "Attestation fra Direktoratet for Fødevareerhverv om behørig afgivelse af erklæringer i medfør af landbrugslovens erhvervsbestemmelser",
      "13": "Godkendelse fra Kirkeministeriet eller Stiftsøvrigheden til salg eller pantsætning af jordelig kirkelig gods",
      "14": "Tilladelse til erhvervelse af arbejderbolig",
      "15": "Stiftelsesdokumenter for selskab under stiftelse",
      "16": "Anden dokumentation for, at anmeldelse af nystiftet selskab er sket overfor Erhvervs- og Selskabsstyrelsen indenfor de i lovens krævede frister",
      "17": "Kommunalbestyrelsens samtykke til salg af ejendom fra Almen boligorganisation",
      "18": "Matrikelkort med attestationen \"e-rids\" (ved salg af nyudstykket parcel)",
      "19": "Dokumentation for generalforsamling om tegningsregler m.v. for forening",
      "20": "Vedtægter m.v. for forening",
      "21": "Interessentskabskontrakt med tegningsregler m.v.",
      "22": "Dispensation eller tilladelse fra Jordbrugskommissionen vedr. erhvervelse af landbrugsejendom",
      "23": "Skiftefuldmagter, udstedt før 25. marts 2008",
      "24": "Notering af umyndiggørelse på fast ejendom",
      "25": "Notering af ægtepagt som særeje",
      "26": "Tinglysning af ægtepagt som adkomst",
      "27": "Meddelelse om konkurs, udstedt før 25. marts 2008",
      "28": "Samme vedr. gældssanering, tvangsakkord",
      "29": "Tinglysning af vielsesattest rådighedsindskrænkende",
      "30": "Prokura, gældende før 25. marts 2008",
      "31": "Samtykke fra indehaver af forkøbsret eller tilbagekøbsret",
      "32": "Original bebestyrerattest, hvis en ejendom sælges af bobestyrer",
      "33": "Original skifteretsattest, hvis ejendom sælges af et dødsbo",
      "34": "Godkendelse fra kommunalbestyrelsen til tinglysning af servitut, jf. planlovens § 42",
      "35": "Samtykke fra bortforpagtning af landbrugsejendom fra Direktoratet for Fødevareerhverv",
      "36": "Godkendelse af pantsætning fra værge",
      "37": "Godkendelse af pantsætning fra Almennyttige boligselskaber",
      "38": "GML-dokument med koordinater til servitutter og bygning på lejet grund",
      "80": "Andet",
      "90": "Intern brug"
   },
   en: {
      "1": "Certificate of Execution issued prior to 25 March 2008",
      "2": "Certificate of Attachment issued prior to 25 March 2008",
      "3": "Judgment concerning real property, with endorsement of appeal, rendered prior to 25 March 2008",
      "4": "Modification judgment, with endorsement of appeal, rendered prior to 25 March 2008",
      "5": "Deed of Auction, including any Deed of Assignment",
      "6": "Certificate of Estate Administration issued prior to 25 March 2008",
      "7": "Trustee’s Certificate issued prior to 25 March 2008",
      "8": "Permit for acquisition of real property in Denmark issued by the Ministry of Justice",
      "9": "Permit for acquisition issued by the County Authority (Statsamt)",
      "10": "Authorization from the Danish Forest and Nature Agency regarding corporate acquisition of real property",
      "11": "Documentation from the Parish Council concerning the Council’s signing authority",
      "12": "Attestation from the Directorate for Food Business confirming proper submission of declarations pursuant to the agricultural law’s business provisions",
      "13": "Approval from the Ministry of Ecclesiastical Affairs or the Diocesan Authority for sale or mortgaging of church-owned land",
      "14": "Permit for acquisition of workers’ housing",
      "15": "Founding documents for a company under incorporation",
      "16": "Other documentation evidencing that notification of a newly incorporated company has been duly submitted to the Danish Commerce and Companies Agency within the statutory deadlines",
      "17": "Consent from the Municipal Council for sale of property by a Public Housing Organization",
      "18": "Cadastral map with the endorsement 'e-plan' (for sale of newly subdivided parcel)",
      "19": "Documentation from the general assembly concerning the association’s signing authority, etc.",
      "20": "Articles of Association and related documents for an association",
      "21": "Partnership Agreement including signing authority, etc.",
      "22": "Exemption or permit from the Agricultural Commission concerning acquisition of agricultural property",
      "23": "Probate Power of Attorney issued prior to 25 March 2008",
      "24": "Registration of legal incapacitation relating to real property",
      "25": "Registration of prenuptial agreement establishing separate property",
      "26": "Registration of prenuptial agreement as title",
      "27": "Notice of bankruptcy issued prior to 25 March 2008",
      "28": "Same concerning debt restructuring or compulsory composition",
      "29": "Registration of marriage certificate restricting right of disposal",
      "30": "Power of Procuration valid prior to 25 March 2008",
      "31": "Consent from holder of pre-emption or repurchase right",
      "32": "Original Certificate of Estate Administration where property is sold by an estate administrator",
      "33": "Original Certificate from the Probate Court where property is sold by an estate",
      "34": "Approval from the Municipal Council for registration of easement pursuant to section 42 of the Danish Planning Act",
      "35": "Consent from the Directorate for Food Business concerning lease of agricultural property",
      "36": "Approval of mortgage granted by legal guardian",
      "37": "Approval of mortgage granted by non-profit housing associations",
      "38": "GML document containing coordinates for easements and buildings on leased land",
      "80": "Other",
      "90": "Internal use"
   }
}

function TLCParser(key: string, value: string): string {
   switch (key) {
      case "Adkomsttype":
      case "Handelstype":
         return titleValParser(value);
   }
   return value + "...";
}

